/**
 * Firebase Client SDK initialization
 *
 * Single initialization point. Import from @copapp/firebase/client
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getFirebaseClientConfig } from './config.js';

let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let functions: Functions;

export function initFirebaseClient(): {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  functions: Functions;
} {
  if (getApps().length === 0) {
    const config = getFirebaseClientConfig();
    app = initializeApp(config);

    // App Check activates as soon as a reCAPTCHA site key is configured.
    // Enforcement is a separate console decision; this side is ready first.
    const appCheckKey = typeof import.meta !== 'undefined' && import.meta.env
      ? (import.meta.env['VITE_APPCHECK_SITE_KEY'] as string | undefined)
      : process.env['VITE_APPCHECK_SITE_KEY'];
    if (appCheckKey) {
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(appCheckKey),
        isTokenAutoRefreshEnabled: true,
      });
    }

    auth = getAuth(app);

    const useEmulators = typeof import.meta !== 'undefined' && import.meta.env
      ? import.meta.env['VITE_USE_EMULATORS'] === 'true'
      : process.env['VITE_USE_EMULATORS'] === 'true';

    // Against the emulator, WebChannel Listen streams can silently die in
    // some browser environments (headless included) — the first snapshot
    // arrives, pushed updates never do. Long-polling makes listeners
    // reliable there; production keeps the default transport.
    if (useEmulators) {
      db = initializeFirestore(app, { experimentalForceLongPolling: true }, config.databaseName);
    } else {
      // Offline persistence (2026-08-17 failure-surfaces pass): an officer
      // losing coverage mid-shift keeps cached reads (history, dashboard)
      // instead of a wall of spinners. Multi-tab so a second tab doesn't
      // break the lease. IndexedDB can be unavailable (private windows,
      // ancient browsers) — fall back to the memory default rather than die.
      try {
        db = initializeFirestore(app, {
          localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
        }, config.databaseName);
      } catch {
        db = getFirestore(app, config.databaseName);
      }
    }
    functions = getFunctions(app);

    if (useEmulators) {
      connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
      connectFirestoreEmulator(db, '127.0.0.1', 8080);
      connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    }
  }

  return { app, auth, db, functions };
}

export { getFirebaseClientConfig } from './config.js';
